import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { GTM } from '@/utils/gtm';
import { Loader2, Mail, Lock, User } from 'lucide-react';
import GoogleLoginButton from '@/components/GoogleLoginButton';
import { clearHasKennelCache } from '@/hooks/useHasKennel';

export default function Register() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Post-register routing per `?flow=` (continuation z `/dodaj-hodowle?flow=...`)
  const flow = searchParams.get('flow'); // 'miot' | 'reproduktor' | null

  const flowDestination = (() => {
    if (flow === 'miot') return '/wizard/miot';
    if (flow === 'reproduktor') return '/wizard/reproduktor';
    // Domyślnie: user wybiera świat (hodowca / biznes) — system dopasuje panele.
    return '/onboarding/profile-type';
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t('register.passwordMinLength', 'Hasło musi mieć minimum 6 znaków'));
      return;
    }
    if (!acceptTerms) {
      toast.error(t('register.acceptTermsError', 'Musisz zaakceptować Regulamin i Politykę Prywatności'));
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      GTM.signUp('email');
      clearHasKennelCache();
      // Specific success copy per flow continuation
      if (flow === 'miot') {
        toast.success('Konto utworzone! Teraz dodaj pierwszego miota 🐶');
      } else if (flow === 'reproduktor') {
        toast.success('Konto utworzone! Teraz dodaj reproduktora 🧬');
      } else {
        toast.success(t('register.success', 'Konto utworzone! Witamy w ApisAI.'));
      }
      navigate(flowDestination);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('register.error', 'Błąd rejestracji'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060610] flex items-center justify-center px-6 relative overflow-hidden">
      {/* Grid + Glow Background — matching Login.js / Landing */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-950/20 via-[#060610] to-[#060610]" />
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-orange-600/[0.08] rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[300px] h-[300px] bg-orange-500/[0.06] rounded-full blur-[100px]" />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,.15) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>


      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src="/logo.svg" alt="ApisAI" className="h-10" />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
            Zacznij zdobywać klientów dla swojej{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              firmy lub hodowli
            </span>
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base">
            Dodaj ofertę — pokażemy ją klientom i pomożemy zamienić zapytania w sprzedaż
          </p>
        </div>

        {/* Glass Card */}
        <div className="backdrop-blur-xl bg-[#0c0c1a]/80 border border-orange-500/15 rounded-2xl p-8 shadow-2xl shadow-orange-500/5">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300 text-sm">{t('auth.name')}</Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('auth.namePlaceholder')}
                  required
                  className="pl-11 bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300 text-sm">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  className="pl-11 bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300 text-sm">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('register.passwordPlaceholder', '••••••••')}
                  required
                  className="pl-11 bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                  data-testid="register-password"
                />
              </div>
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
                className="mt-1 border-white/20 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                data-testid="register-terms"
              />
              <label htmlFor="terms" className="text-sm text-zinc-400 cursor-pointer">
                {t('register.iAccept', 'Akceptuję')}{' '}
                <Link to="/regulamin" className="text-orange-400 hover:underline" target="_blank">
                  {t('register.terms', 'Regulamin')}
                </Link>{' '}
                {t('common.and', 'i')}{' '}
                <Link to="/polityka-prywatnosci" className="text-orange-400 hover:underline" target="_blank">
                  {t('register.privacyPolicy', 'Politykę Prywatności')}
                </Link>
              </label>
            </div>

            {/* Main CTA Button */}
            <Button
              type="submit"
              disabled={loading || !acceptTerms}
              className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:shadow-orange-500/30 rounded-xl h-12 text-base font-bold disabled:opacity-50 transition-all"
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Utwórz konto i zacznij zdobywać klientów'}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-zinc-500 text-sm">{t('common.or', 'lub')}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Google Login */}
          <GoogleLoginButton label="Kontynuuj z Google" />

          {/* Trust line */}
          <p className="text-center text-zinc-500 text-xs mt-5">
            Start w 5 minut. Bez umowy.
          </p>

          {/* Already have account */}
          <div className="mt-5 text-center">
            <p className="text-zinc-400">
              Masz już konto?{' '}
              <Link to="/logowanie" className="text-orange-400 hover:text-orange-300 font-medium transition-colors" data-testid="register-login-link">
                Zaloguj się
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-zinc-500 hover:text-gray-300 text-sm transition-colors">
            ← {t('common.backToHome', 'Wróć na stronę główną')}
          </Link>
        </div>

        {/* WhatsApp Onboarding CTA */}
        <div className="mt-5 text-center">
          <a
            href="https://wa.me/48507334540?text=Cze%C5%9B%C4%87!%20Chc%C4%99%20za%C5%82o%C5%BCy%C4%87%20konto%20w%20ApisAI.%20Mo%C5%BCecie%20mi%20pom%C3%B3c%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20 hover:bg-[#25D366]/20 transition-all text-[#25D366] text-sm font-medium"
            data-testid="whatsapp-onboarding-btn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Nie chcesz się rejestrować? Napisz na WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  );
}
